import { personalRangeError, shiftDate } from "./timetablePersonalWeek";

export type WeeklyGenerationProgress = {
  phase: "saving" | "retrying" | "paused" | "complete";
  completedWeeks: number;
  totalWeeks: number;
  start: string;
  end: string;
  count: number;
  startedAt: number;
};
export type WeeklyGenerationOptions = {
  onProgress?: (progress: WeeklyGenerationProgress) => void;
  signal?: AbortSignal;
};
export type WeeklyBatchResult = {
  success: boolean;
  count?: number;
  error?: string;
  retryable?: boolean;
};
export type WeeklyGenerationResult = WeeklyBatchResult & {
  completedWeeks: number;
};

export function weeklyGenerationBatches(start: string, end: string) {
  const issue = personalRangeError(start, end);
  if (issue) throw new Error(issue);
  const batches: { start: string; end: string }[] = [];
  for (let date = start; date <= end; date = shiftDate(date, 7)) {
    const last = shiftDate(date, 6) < end ? shiftDate(date, 6) : end;
    const weekdays = Array.from({ length: 7 }, (_, i) =>
      shiftDate(date, i),
    ).filter(
      (d) =>
        d <= last && ![0, 6].includes(new Date(d + "T00:00:00Z").getUTCDay()),
    );
    if (weekdays.length)
      batches.push({ start: weekdays[0], end: weekdays.at(-1)! });
  }
  if (!batches.length)
    throw new Error("수업이 있는 월~금 날짜를 포함해 주세요.");
  return batches;
}

// The server commits one batch atomically and remembers completed dates. A lost
// response can therefore be retried without duplicating or restoring edited lessons.
export async function generateWeeklyTimetableBatches(
  start: string,
  end: string,
  save: (start: string, end: string) => Promise<WeeklyBatchResult>,
  options: WeeklyGenerationOptions = {},
  wait: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<WeeklyGenerationResult> {
  let batches;
  try {
    batches = weeklyGenerationBatches(start, end);
  } catch (e) {
    return { success: false, completedWeeks: 0, error: (e as Error).message };
  }
  let count = 0,
    completedWeeks = 0;
  const startedAt = Date.now();
  const report = (
    phase: WeeklyGenerationProgress["phase"],
    batch: { start: string; end: string },
  ) =>
    options.onProgress?.({
      phase,
      completedWeeks,
      totalWeeks: batches.length,
      ...batch,
      count,
      startedAt,
    });
  for (const batch of batches) {
    let result: WeeklyBatchResult = { success: false };
    for (let attempt = 0; attempt < 3; attempt++) {
      if (options.signal?.aborted) break;
      report(attempt ? "retrying" : "saving", batch);
      if (attempt) await wait(attempt * 1000);
      if (options.signal?.aborted) break;
      try {
        result = await save(batch.start, batch.end);
      } catch {
        result = { success: false, retryable: true };
      }
      if (result.success || !result.retryable) break;
    }
    if (!result.success) {
      report("paused", batch);
      return {
        success: false,
        count,
        completedWeeks,
        error:
          result.error ||
          "연결이 원활하지 않아 잠시 멈췄어요. 다시 누르면 저장한 날짜는 유지하고 이어서 만들어요.",
      };
    }
    count += result.count || 0;
    completedWeeks++;
    report(completedWeeks === batches.length ? "complete" : "saving", batch);
  }
  return { success: true, count, completedWeeks };
}
